import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface AccountingPeriod {
    name: string;
    code: string;
    enabled: boolean;
    startMonthId: number;
    endMonthId: number;
    id: number;
    isDefault: boolean;
    isFinancialYear: boolean;
}

export interface AccountingPeriodWithMonths extends AccountingPeriod {
    startMonthName: string;
    endMonthName: string;
}

@Injectable({
    providedIn: 'root'
})
export class AccountingPeriodService {
    private readonly apiUrl = `${environment.inventoryURl}InventorySettings/AccountingPeriodSetting`;
    private accountingPeriodSubject = new BehaviorSubject<AccountingPeriodWithMonths | null>(null);
    public accountingPeriod$ = this.accountingPeriodSubject.asObservable();

    private readonly months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    constructor(private http: HttpClient) { }

    async fetchAccountingPeriodSetting(): Promise<AccountingPeriodWithMonths | null> {
        const cachedData = this.accountingPeriodSubject.getValue();

        if (cachedData) {
            return cachedData;
        } else {
            try {
                const response = await this.http.get<any>(this.apiUrl).toPromise();

                if (response?.result?.item1) {
                    // Financial Year is enabled, use the settings from item2
                    const accountingPeriod: AccountingPeriod = {
                        name: response.result.item2.name,
                        code: response.result.item2.code,
                        enabled: response.result.item2.enabled,
                        startMonthId: response.result.item2.startMonthId,
                        endMonthId: response.result.item2.endMonthId,
                        id: response.result.item2.id,
                        isDefault: response.result.item2.isDefault,
                        isFinancialYear: true
                    };
                    const periodWithMonths = this.addMonthNames(accountingPeriod);
                    this.accountingPeriodSubject.next(periodWithMonths);
                    return periodWithMonths;
                } else {
                    // Calendar Year (Jan-Dec) is used when item1 is false
                    const accountingPeriod: AccountingPeriod = {
                        name: 'Calendar Year',
                        code: 'calendaryear',
                        enabled: true,
                        startMonthId: 1, // January
                        endMonthId: 12,  // December
                        isDefault: true,
                        id: 0,
                        isFinancialYear: false
                    };
                    const periodWithMonths = this.addMonthNames(accountingPeriod);
                    this.accountingPeriodSubject.next(periodWithMonths);
                    return periodWithMonths;
                }
            } catch (err) {
                // Default to Calendar Year in case of error
                const defaultAccountingPeriod: AccountingPeriod = {
                    name: 'Calendar Year',
                    code: 'calendaryear',
                    enabled: true,
                    startMonthId: 1, // January
                    endMonthId: 12,  // December
                    isDefault: true,
                    id: 0,
                    isFinancialYear: false
                };
                const periodWithMonths = this.addMonthNames(defaultAccountingPeriod);
                this.accountingPeriodSubject.next(periodWithMonths);
                return periodWithMonths;
            }
        }
    }

    clearAccountingPeriod() {
        this.accountingPeriodSubject.next(null);
    }

    get currentAccountingPeriod(): AccountingPeriodWithMonths | null {
        return this.accountingPeriodSubject.value;
    }

    private getMonthName(monthId: number): string {
        // monthId is 1-based, array is 0-based
        return this.months[monthId - 1] || '';
    }

    private addMonthNames(period: AccountingPeriod): AccountingPeriodWithMonths {
        return {
            ...period,
            startMonthName: this.getMonthName(period.startMonthId),
            endMonthName: this.getMonthName(period.endMonthId)
        };
    }
}